import { useEffect, useRef, useState, type ReactNode } from 'react';
import { splitContactName, titleCaseName, type ConfirmedBy } from '@bbbk/schema';
import { Avatar, CareTile, Icon, ModalFooter, PaneHeader, saveBlockers, useToast, useShellDismiss, useSavedDismiss, writeWithToast, useKeepOpen, useDirtyDraft, useUnsavedGuard, DisclosureRegion } from '../common';
import type { OwnerCreateDefaults, OwnerFormDog } from '../viewings/convert';
import { api } from '../../lib/api';
import type { Owner } from '../bookings/types';
import { ProofFull } from '../bookings/ProofImage';

// THE ID IS PART OF THE DRAFT, not decoration. A household save reconciles
// contacts by id; a contact that goes back without one is read as somebody new,
// and the row it should have updated is deleted — taking that person's
// registration link and their binding with it (household-app-access, 2026-09-12).
interface ContactDraft { id?: number; firstName: string; lastName: string; phone: string; email: string; registeredAt?: string | null; confirmedBy?: ConfirmedBy | null }
interface DogDraft {
  id?: string;
  name: string;
  breed: string;
  size: string;
  vaccinesDue: string;
  /** The newest card the customer uploaded, so the desk can open it (deposit-proof AC15). Read-only here. */
  cardProofId?: string | null;
  cardProofMime?: string | null;
  /**
   * ON IS NOT THE SAME AS HAVING WORDS YET.
   *
   * The tile's state used to be derived from the instructions, so turning it on
   * had to invent a value to keep the field visible — it wrote the literal
   * words "On medication" into the column (Freddi, 2026-09-16). The dogs step
   * has always modelled these as two things, a None/Yes segment and the
   * instructions beside it, and so does this now: on, then empty, with a
   * placeholder telling them what to type.
   */
  medsOn: boolean;
  /** The instructions themselves. '' while they have not been typed. */
  meds: string;
  keepApart?: boolean | null;
  confirmedBy?: ConfirmedBy | null;
}

// WHO TOLD US THIS. An 18px avatar of the contact who gave the value, sitting
// at the end of the label row — the placement and the size Freddi settled in
// the ceremony (docs/decision/desk-sees-customer-answers-provenance-model.md).
//
// It renders ONLY where a customer write actually landed on that field (AC1):
// a registered contact does not make their whole row marked, and a household
// nobody registered carries no marks at all (AC2).
//
// `by` is an owner_contacts.id, so the avatar is the CONTACT's own — two people
// on one household can both register, and a generic portal glyph could not say
// which of them (AC3).
// "12 Sep" — the pill says WHEN, because *Registered* alone leaves Patricia
// wondering whether it was this morning or in March. Irish locale, like the
// rest of the desk.
function shortDate(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-IE', { day: 'numeric', month: 'short' });
}

// A MARK IS A CLAIM, AND IT DIES WHEN PATRICIA OVERWRITES THE VALUE (AC4).
//
// Only the keys this change actually MOVES are dropped — a patch that touches
// `phone` must not retract a claim about `firstName`. The server holds the same
// line on save (`lib/confirmed-by.ts`); this is the half she can see, so the
// avatar goes as she types rather than on reload.
function dropMarks<T extends object>(
  marks: ConfirmedBy | null | undefined,
  before: T,
  patch: Partial<T>,
): ConfirmedBy | null {
  if (!marks) return marks ?? null;
  const out = { ...marks };
  let moved = false;
  for (const key of Object.keys(patch) as (keyof T & string)[]) {
    if (!(key in out)) continue;
    if (patch[key] === before[key]) continue;
    delete out[key];
    moved = true;
  }
  return moved ? out : marks;
}

function Mark({ by, contacts }: { by?: number; contacts: ContactDraft[] }) {
  if (by == null) return null;
  const who = contacts.find((c) => c.id === by);
  const name = [who?.firstName, who?.lastName].filter(Boolean).join(' ').trim();
  return (
    <span className="cust-mark" title={`${name || 'The household'} gave us this`}>
      <Avatar name={name || '?'} id={String(by)} size={18} />
    </span>
  );
}

// Collapsible card section header — keeps the card look while letting Contacts /
// Dogs expand and contract (both open by default). When collapsed it shows the
// avatar + name chips so the household reads at a glance without expanding.
const SectionHead = ({ label, summary, open, onToggle }: { label: string; summary: ReactNode; open: boolean; onToggle: () => void }) => (
  <button type="button" className={'sh sh-toggle ' + (open ? 'open' : 'closed')} onClick={onToggle}>
    <span className="sh-label">{label}</span>
    {!open && <span className="sh-sum">{summary}</span>}
    <svg className="sh-chev" width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M4 6l4 4 4-4" /></svg>
  </button>
);

// Avatar + name chips for a collapsed section summary.
const chips = (items: { key: string; name: string }[], kind: 'person' | 'dog'): ReactNode =>
  items.filter((i) => i.name.trim()).map((i) => (
    <span key={i.key} className="sumdog"><Avatar name={i.name} id={i.key} kind={kind} size={18} />{i.name}</span>
  ));

export interface OwnerFormProps {
  owner: Owner | null; // null = create a new household
  // CREATE MODE, ALREADY FILLED IN — the contact, their phone and the dog rows a
  // viewing already knew (book-them-in AC1/AC2). Its create mode previously took
  // `owner={null}` and seeded one blank contact with no dogs, and there was no
  // prop through which any of this could be supplied; mounting it unchanged
  // would have produced an empty form. Ignored on an edit.
  createDefaults?: OwnerCreateDefaults;
  archived?: boolean; // carried by the context bundle, so no refetch on open
  // A create passes the new owner id AND the dog rows as saved, so a caller
  // converting a viewing can carry the names given here back to it.
  // `stay` — the keep-open tick was on: the record is saved and the modal is not
  // to close. The host revalidates in place and shows the Saved mark.
  onSaved: (createdOwnerId?: string, dogs?: OwnerFormDog[], opts?: { stay?: boolean }) => void;
  onClose: () => void;
}

// Edit/New owner pane — sectioned in cards like the booking pane (Household /
// Contacts / Dogs), each always visible. Contacts replace wholesale on save; dogs
// are individual records, so a removed dog is DELETEd on save.
export function OwnerForm({ owner, archived, createDefaults, onSaved, onClose }: OwnerFormProps) {
  // See BookingForm: the shell plays the exit, `onClose` alone does not.
  const close = useShellDismiss(onClose);
  const [cardOpen, setCardOpen] = useState<{ id: string; name: string; mime?: string | null } | null>(null);
  // The close after a commit — Save's close-first, Archive — releases the guard.
  const closeSaved = useSavedDismiss(onClose);
  const isCreate = !owner;
  const [contacts, setContacts] = useState<ContactDraft[]>(
    owner && owner.contacts.length
      ? owner.contacts.map((c) => ({
          id: c.id,
          // Split on read for any contact stored before the two fields existed:
          // the columns are backfilled (0024) but a row written by a script
          // since then may carry only `name`.
          firstName: c.firstName ?? splitContactName(c.name ?? '').firstName ?? '',
          lastName: c.lastName ?? splitContactName(c.name ?? '').lastName ?? '',
          phone: c.phone ?? '',
          email: c.email ?? '',
          registeredAt: c.registeredAt ?? null,
          confirmedBy: c.confirmedBy ?? null,
        }))
      : // A create with values from a viewing carries them; a plain one opens with
        // ONE BLANK ROW, as it always has — a form with no contact row at all
        // gives the user nothing to type into.
        (createDefaults?.contacts?.length
          ? createDefaults.contacts.map((c) => ({
              firstName: splitContactName(c.name ?? '').firstName ?? '',
              lastName: splitContactName(c.name ?? '').lastName ?? '',
              phone: c.phone ?? '',
              email: c.email ?? '',
            }))
          : [{ firstName: '', lastName: '', phone: '', email: '' }]),
  );
  const [address, setAddress] = useState(owner?.address ?? createDefaults?.address ?? '');
  // The household address is customer-written too (writeDetails does
  // UPDATE owners SET address), so it carries a mark and loses it on an edit.
  const [addressMark, setAddressMark] = useState<ConfirmedBy | null>(owner?.confirmedBy ?? null);

  const [dogList, setDogList] = useState<DogDraft[]>(
    owner
      ? owner.dogs.map((d) => ({
          id: d.id,
          name: d.name ?? '',
          breed: d.breed ?? '',
          size: d.size ?? 'big',
          // THE COLUMN, AND NOTHING BESIDE IT. Registration writes the card's
          // dates onto the dog (2026-09-16) and marks `vaccinesDue` in the same
          // map as name and breed, so the field reads what Patricia can edit
          // and the mark says who gave it. Preferring a side-channel claim
          // here let any Save write it over a date she had since corrected
          // (claude[bot] P1, PR #231).
          vaccinesDue: d.vaccinesDue ?? '',
          cardProofId: d.cardProofId ?? null,
          cardProofMime: d.cardProofMime ?? null,
          medsOn: d.meds != null,
          meds: d.meds ?? '',
          keepApart: d.keepApart ?? null,
          confirmedBy: d.confirmedBy ?? null,
        }))
      : (createDefaults?.dogs ?? []).map((d) => ({ ...d, medsOn: false, meds: '' })),
  );
  // Persisted dogs the user removed — DELETEd on save.
  const [removedDogIds, setRemovedDogIds] = useState<string[]>([]);
  const [openContacts, setOpenContacts] = useState(true);
  const [openDogs, setOpenDogs] = useState(true);
  // archived + address + doNotMix all arrive on the owner prop (the context
  // bundle), so the pane no longer refetches the owner on open.
  const [isArchived, setIsArchived] = useState(archived ?? false);
  // The unsaved-changes guard's one line: the fields a save writes, and nothing
  // else — so opening a section is never a change.
  const { dirty, markSaved, markSavedAs } = useDirtyDraft('owner', 'this household', { contacts, address, dogList, removedDogIds, isArchived });

  // A DATE WRITTEN BESIDE THE FORM REACHES IT (Freddi, 2026-10-13: "if patricia changes the vaccination
  // date at the desk that should update for that household across the board … like when she is
  // accepting a vaccination card"). The household pane holds the Vaccinations card too, and an accept
  // there writes the dog's date and re-reads the household — but the draft was copied once, on open,
  // so the form went on showing the old date and a Save wrote it back over the accept. Now a dog whose
  // stored date moved takes the new one, unless Patricia has typed in that field herself; a form that
  // was clean stays clean, so leaving it asks nothing.
  const seenDates = useRef<Record<string, string>>(Object.fromEntries((owner?.dogs ?? []).map((d) => [d.id, d.vaccinesDue ?? ''])));
  useEffect(() => {
    if (!owner) return;
    const moved = owner.dogs.filter((d) => (d.vaccinesDue ?? '') !== (seenDates.current[d.id] ?? (d.vaccinesDue ?? '')));
    if (moved.length === 0) return;
    const next = dogList.map((x) => {
      const m = moved.find((d) => d.id === x.id);
      return m && x.vaccinesDue === seenDates.current[m.id] ? { ...x, vaccinesDue: m.vaccinesDue ?? '' } : x;
    });
    for (const d of moved) seenDates.current[d.id] = d.vaccinesDue ?? '';
    if (!dirty) markSavedAs({ contacts, address, dogList: next, removedDogIds, isArchived });
    setDogList(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the stored dates are the trigger; the draft is read, not watched
  }, [owner]);
  const keepOpen = useKeepOpen();
  const toast = useToast();

  const updC = (i: number, p: Partial<ContactDraft>) =>
    setContacts((prev) => prev.map((c, j) => (j === i ? { ...c, ...p, confirmedBy: dropMarks(c.confirmedBy, c, p) } : c)));
  const updD = (i: number, p: Partial<DogDraft>) =>
    setDogList((prev) => prev.map((d, j) => (j === i ? { ...d, ...p, confirmedBy: dropMarks(d.confirmedBy, d, p) } : d)));
  const removeContact = (i: number) => setContacts((prev) => prev.filter((_, j) => j !== i));
  const removeDog = (i: number) => {
    const d = dogList[i];
    if (d?.id) setRemovedDogIds((prev) => [...prev, d.id!]);
    setDogList((prev) => prev.filter((_, j) => j !== i));
  };

  // On CREATE the modal stays open through the await (see below), so guard against
  // a double-click POSTing two owners. Reset on failure so a retry is allowed.
  const savingRef = useRef(false);
  // ONE KEY PER ATTEMPTED CREATE, kept across a failed try and cleared on
  // success — so a reply lost after the server committed comes back as the
  // household it made rather than a second one (see POST /api/owners).
  const createKeyRef = useRef<string | null>(null);
  const save = () => {
    if (savingRef.current) return;
    const clean = contacts
      .map((c) => ({
        id: c.id,
        firstName: c.firstName.trim(),
        lastName: c.lastName.trim(),
        phone: c.phone.trim(),
        email: c.email.trim(),
      }))
      // A contact needs a NAME of some kind; the surname alone counts, because a
      // row that says something is never discarded in silence.
      .filter((c) => c.firstName || c.lastName);
    if (clean.length === 0) { toast.error('At least one contact needs a name.'); return; }
    savingRef.current = true;
    // Capture the drafts. On EDIT we close instantly and write in the background.
    // On CREATE we must NOT close first: onSaved (onOwnerCreated) opens the new
    // owner and drops into a new booking, and that flow needs the modal still
    // mounted and the new owner's id from the POST (Codex P1).
    const dogs = dogList, removed = removedDogIds, addr = address.trim();
    // KEEP OPEN (G14): an edit with the tick on neither closes first nor after;
    // the host is told `stay` and shows the Saved mark. A create already stays
    // on the household (Freddi, 2026-09-12) — the tick changes nothing there.
    const stay = !!owner && keepOpen.on;
    void writeWithToast(toast, {
      // EDIT closes instantly and writes in the background; CREATE must NOT close
      // first — onSaved (onOwnerCreated) opens the new owner and needs the modal
      // still mounted plus the new owner id from the POST (Codex P1).
      closeFirst: owner && !stay ? closeSaved : undefined,
      write: async () => {
        const payloadOf = (d: DogDraft) => ({
          name: d.name.trim(),
          breed: d.breed.trim() || null,
          size: d.size,
          vaccinesDue: d.vaccinesDue.trim() || null,
          // The tile decides whether there IS medication; the words are what it
          // is. Off sends null whatever is in the box, and on with nothing
          // typed sends null too — an empty instruction is not an instruction.
          meds: d.medsOn ? d.meds.trim() || null : null,
          // NULL MEANS NOBODY HAS ANSWERED and is a real value, not an absence
          // (AC10). Sent explicitly so a dog Patricia un-ticks is stored as
          // *mixes well* rather than falling back to the customer's answer.
          keepApart: d.keepApart ?? null,
        });
        let ownerId: string;
        if (owner) {
          await api(`/owners/${owner.id}`, { method: 'PATCH', body: { address: addr, contacts: clean } });
          ownerId = owner.id;
          // EDIT keeps the per-dog writes: they are individual records, some
          // persisted and some new, and there is no single statement that means
          // "reconcile these". The create's fault was never the loop itself —
          // it was that a mid-loop failure left a HALF-CREATED HOUSEHOLD nothing
          // could reconcile. An edit's half-applied change is visible on a
          // household that already exists and is corrected by saving again.
          for (const d of dogs) {
            const p = payloadOf(d);
            if (d.id) await api(`/dogs/${d.id}`, { method: 'PATCH', body: p });
            else await api('/dogs', { method: 'POST', body: { ...p, ownerId } });
          }
        } else {
          // CREATE IS ONE WRITE — household, contacts and dogs together, in one
          // transaction server-side (docs/architecture/book-them-in/, ADR-1).
          //
          // It used to POST the owner and then POST each dog in a loop: a dog
          // failing mid-loop left the owner and the earlier dogs committed,
          // reported an error, cleared the guard below — and the retry created a
          // SECOND household with the first one's dogs orphaned onto it.
          //
          // READS THE RESPONSE, so it must ask for one. Writes default to
          // `Prefer: return=minimal` app-wide because nothing else reads a
          // write's result; this one needs the new id to open the household
          // straight afterwards.
          //
          // NO ROW IS DROPPED HERE. The old `if (!d.name.trim()) continue` is
          // gone — a nameless row is refused by `blockers` above, on screen,
          // with its breed still showing, rather than discarded in silence.
          createKeyRef.current ??= crypto.randomUUID();
          const res = await api<{ id: string }>('/owners', {
            method: 'POST',
            wantResult: true,
            idempotencyKey: createKeyRef.current,
            body: { address: addr, contacts: clean, dogs: dogs.filter((d) => d.name.trim()).map(payloadOf) },
          });
          createKeyRef.current = null;
          ownerId = res.data.id;
        }
        // Delete the dogs the user removed. Removal is never blocked now — the API
        // takes the dog off any bookings first (a stay can be left with zero dogs).
        for (const id of removed) await api(`/dogs/${id}`, { method: 'DELETE' });
        if (stay) {
          markSaved();
          savingRef.current = false;
        }
        onSaved(owner ? undefined : ownerId, owner ? undefined : dogs, stay ? { stay: true } : undefined);
      },
      success: isCreate ? 'Household created' : 'Household updated',
      error: 'Could not save the household.',
      onError: () => { savingRef.current = false; }, // allow a retry (create stays open on failure)
    });
  };

  // Archive and Restore write the archival state and nothing else, then close
  // — so an unsaved address, contact or dog edit would have gone with the
  // close, released as though it had been committed (review on #239). The
  // guard asks first; what she keeps editing, she can still save.
  const { guard } = useUnsavedGuard();
  const toggleArchive = () => guard(() => {
    if (!owner) return;
    void writeWithToast(toast, {
      write: () => isArchived ? api(`/owners/${owner.id}/restore`, { method: 'POST' }) : api(`/owners/${owner.id}`, { method: 'DELETE' }),
      success: isArchived ? 'Household restored' : 'Household archived',
      error: 'Could not update the household.',
      onSaved: () => { onSaved(); closeSaved(); },
    });
  });

  // A DOG ROW IS NEVER SILENTLY DISCARDED (book-them-in AC7b). `save` used to
  // run `if (!d.name.trim()) continue`, so a row carrying a breed and no name
  // vanished without a word — and a viewing may legitimately record exactly that
  // row. Now the row stays on screen with its breed intact and Save says which
  // one is missing a name. `dogs.name` is NOT NULL at the column, so this is
  // also what keeps the create from 500ing where it ought to explain itself.
  // A WHOLLY BLANK ROW SAYS NOTHING and is not a dog somebody forgot to name —
  // it is the empty row a fresh form (or a viewing with no dogs) opens with, and
  // it is dropped at save exactly as it always was. What blocks is a row that
  // says something — a breed, a date — and lacks the one thing a dog cannot be
  // saved without (review, PR #174).
  // Medication and keep-apart count too (PR #231 review): a row with
  // instructions typed and no name is a dog somebody forgot to name, not the
  // blank row a fresh form opens with.
  const saysSomething = (d: DogDraft) => !!(d.breed.trim() || d.vaccinesDue.trim() || d.meds.trim() || d.keepApart === true);
  const nameless = dogList.filter((d) => !d.name.trim() && saysSomething(d));
  // A SIZE IS NEVER GUESSED EITHER. A row that arrived from a viewing has none
  // chosen, and a named dog without one blocks Save with its name — the size
  // is what the kennel runs on, and a Beagle saved as big in silence is a
  // wrong fact nobody typed (review, PR #174). Rows added by hand keep the
  // form's own default, as they always have.
  const sizeless = dogList.filter((d) => d.name.trim() && !d.size);
  const blockers = saveBlockers(
    !contacts.some((c) => c.firstName.trim() || c.lastName.trim()) && 'A household needs at least one named contact.',
    // THE BREED HINT ONLY IDENTIFIES A ROW WHEN THERE IS ONE ROW TO IDENTIFY.
    // Naming `nameless[0]`'s breed while reporting a count of two points at one
    // of them and says nothing about the other — a user could fill in the
    // Labrador and be left wondering what still blocks Save. And the verb has
    // to agree: "2 dogs still needs a name" (codex, PR #174).
    nameless.length > 0 &&
      (nameless.length === 1
        ? `A dog still needs a name${nameless[0]!.breed.trim() ? ` — the ${nameless[0]!.breed.trim()}` : ''}.`
        : `${nameless.length} dogs still need a name.`),
    sizeless.length > 0 &&
      (sizeless.length === 1
        ? `${sizeless[0]!.name.trim()} needs a size — big or little.`
        : `${sizeless.length} dogs still need a size.`),
    dogList.some((d) => d.medsOn && !d.meds.trim()) && 'Add medication instructions or turn Medication off before saving.',
  );

  const name = [contacts[0]?.firstName, contacts[0]?.lastName].filter(Boolean).join(' ') || owner?.contacts[0]?.name || 'New household';
  const dogsSub = (owner?.dogs ?? []).map((d) => (d.breed ? `${d.name} (${d.breed})` : d.name)).join(', ');

  const cardViewer = cardOpen ? <ProofFull id={cardOpen.id} mime={cardOpen.mime} alt={`${cardOpen.name}’s vaccination card`} onClose={() => setCardOpen(null)} /> : null;
  return (
    <div className="pane anim-fade">
      {cardViewer}
      <div className="pane-scroll">
      {/* THE EXISTING MODAL'S OWN HEADER, whether a viewing opened it or the New
          menu did (Sir, 2026-09-02). The one thing a pre-filled create knows
          that a blank one does not is who it is for, and that shows in the
          avatar's initials; the title and the line beneath are the form's. */}
      <PaneHeader
        avatar={{ name: (isCreate ? createDefaults?.contacts[0]?.name : owner?.contacts[0]?.name) || '?', id: owner?.id ?? 'new' }}
        title={isCreate ? 'New Household' : 'Edit Household'}
        sub={isCreate ? 'Add a new household' : `${name} · ${dogsSub || 'No dogs on file'} · ${owner?.visits ?? 0} stays${isArchived ? ' · archived' : ''}`}
        isNew={isCreate}
      />

      <div className="scard">
        <div className="sh">Household</div>
        {/* The household do-not-mix checkbox was retired (Freddi 2026-07-17):
            keep-apart is a PER-BOOKING care flag (the booking modal's tile).
            Flagged households were ported by migration 0013, and a new booking
            for one opens with Keep apart pre-ticked (BookingForm seeds from
            owner.doNotMix, which is now read-only legacy data). */}
        <div className="field" style={{ margin: 0 }}>
          <label>Address<Mark by={addressMark?.address} contacts={contacts} /></label>
          <input value={address} placeholder="Town, County" onChange={(e) => {
            setAddress(e.target.value);
            setAddressMark((m) => dropMarks(m, { address }, { address: e.target.value }));
          }} />
        </div>
      </div>

      <div className="scard">
        <SectionHead label="Contacts" summary={chips(contacts.map((c, i) => ({ key: `c${i}`, name: [c.firstName, c.lastName].filter(Boolean).join(' ') })), 'person')} open={openContacts} onToggle={() => setOpenContacts((o) => !o)} />
        <DisclosureRegion open={openContacts}>
        {contacts.map((c, i) => (
          <div key={i} className="repeat">
            <div className="repeat-head">
              <span className="repeat-label">Contact {i + 1}</span>
              {/* REGISTRATION STATE BELONGS TO THE PERSON, so it sits here and
                  nowhere else. Registered and not-registered side by side is
                  the legend — nothing has to explain the difference. */}
              <span className={'contact-reg' + (c.registeredAt ? ' yes' : '')}>
                {c.registeredAt ? `Registered ${shortDate(c.registeredAt)}` : 'Not registered'}
              </span>
              {contacts.length > 1 && <button type="button" className="repeat-del" title="Remove contact" onClick={() => removeContact(i)}><Icon name="trash" size={13} /></button>}
            </div>
            {/* TWO FIELDS (R-DESK-18). The surname is optional in the RULE —
                plenty of contacts reach the book as a first name and a mobile,
                and Save never asks for one — but it is not LABELLED optional
                (Freddi, 2026-09-12): the word on a field nobody is being
                stopped by is noise, and it reads as if the other fields were
                compulsory when only a name of some kind is. */}
            <div className="field-row">
              <div className="field"><label>First name<Mark by={c.confirmedBy?.firstName} contacts={contacts} /></label><input value={c.firstName} onChange={(e) => updC(i, { firstName: e.target.value })} onBlur={(e) => updC(i, { firstName: titleCaseName(e.target.value) })} /></div>
              <div className="field"><label>Last name<Mark by={c.confirmedBy?.lastName} contacts={contacts} /></label><input value={c.lastName} onChange={(e) => updC(i, { lastName: e.target.value })} onBlur={(e) => updC(i, { lastName: titleCaseName(e.target.value) })} /></div>
            </div>
            <div className="field-row">
              <div className="field"><label>Phone</label><input value={c.phone} placeholder="+353 87 …" onChange={(e) => updC(i, { phone: e.target.value })} /></div>
              {/* A REGISTERED CONTACT'S EMAIL IS THEIR SIGN-IN IDENTITY.
                  The allowlist is keyed on the address they actually signed in
                  with, so typing over it here would change the display and
                  nothing else — their access keeps working under the old one
                  and Patricia is left reading a record she cannot act on. The
                  route holds the same line; this is the half she can see.
                  Changing it means revoke and re-invite, from App access.

                  A REGISTERED CONTACT WITH NO EMAIL IS NOT LOCKED — there is
                  one in the live data. Nothing to protect, and a padlocked
                  blank is a field nobody could ever fill. */}
              {/* EMAIL IS NEVER THE DESK'S TO TYPE (Freddi, 2026-09-15:
                  *"us entering that value is irrelevant"*).
                  
                  It was a hint and nothing more. There is no email provider in
                  this product — the link goes by WhatsApp — nothing searches on
                  it, and the moment anybody signs in their own address
                  overwrites whatever was here. A guess that is always replaced
                  by the truth is not worth offering a field for.
                  
                  Disabled, not hidden: Patricia still needs to SEE the address
                  a registered customer signs in with, and an empty one is how
                  she knows nobody has yet. */}
              <div className="field">
                <label>Email</label>
                {/* The help is a CHILD of the control, not a sibling of it. As
                    a sibling it inherited the field's own 5px label gap and sat
                    a full row away from the input it describes (Freddi,
                    2026-09-16). Wrapped, the field's gap separates label from
                    control and this one governs input from help. */}
                <div className="owner-contact-email-wrap">
                  <div className="owner-contact-email-locked">
                    <input
                      value={c.email || ''}
                      disabled
                      readOnly
                      placeholder="Set when they sign in"
                      aria-describedby={`email-locked-${i}`}
                      title={c.registeredAt ? 'The address they sign in with' : 'Set by the customer when they sign in'}
                    />
                    {/* No `aria-hidden` here: `Icon` renders it on its own svg
                        and does not spread props, so passing one did nothing at
                        all. The review said the line would fail the typecheck —
                        it does not, because JSX exempts hyphenated attributes
                        from a component's props type. It was dead, not broken. */}
                    <Icon name="lock" size={13} />
                  </div>
                  <span id={`email-locked-${i}`} className="owner-contact-email-why">
                    {c.registeredAt
                      ? 'The address they sign in with. Change it from App access.'
                      : 'Filled in by the customer when they sign in.'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        ))}
        <button type="button" className="owner-contact-add" onClick={() => setContacts((p) => [...p, { firstName: '', lastName: '', phone: '', email: '' }])}>+ Add contact</button>
        </DisclosureRegion>
      </div>

      <div className="scard">
        <SectionHead label="Dogs" summary={chips(dogList.map((d, i) => ({ key: d.id ?? `d${i}`, name: d.name })), 'dog')} open={openDogs} onToggle={() => setOpenDogs((o) => !o)} />
        <DisclosureRegion open={openDogs}>
        {dogList.length === 0 && <div className="empty" style={{ padding: '4px 0 12px' }}>No dogs yet — add the household's dogs below.</div>}
        {dogList.map((d, i) => (
          <div key={d.id ?? i} className="repeat">
            <div className="repeat-head"><span className="repeat-label">{d.name.trim() || `Dog ${i + 1}`}</span><button type="button" className="repeat-del" title="Remove dog" onClick={() => removeDog(i)}><Icon name="trash" size={13} /></button></div>
            <div className="field-row">
              <div className="field"><label>Name<Mark by={d.confirmedBy?.name} contacts={contacts} /></label><input value={d.name} onChange={(e) => updD(i, { name: e.target.value })} onBlur={(e) => updD(i, { name: titleCaseName(e.target.value) })} /></div>
              <div className="field"><label>Breed<Mark by={d.confirmedBy?.breed} contacts={contacts} /></label><input value={d.breed} placeholder="e.g. Labrador" onChange={(e) => updD(i, { breed: e.target.value })} /></div>
            </div>
            <div className="field-row" style={{ marginTop: 2 }}>
              <div className="field"><label>Size</label><div className="seg">{['big', 'little'].map((s) => <button key={s} type="button" className={d.size === s ? 'active' : ''} onClick={() => updD(i, { size: s })}>{s.charAt(0).toUpperCase() + s.slice(1)}</button>)}</div></div>
              <div className="field">
                <label>
                  Vaccinations due<Mark by={d.confirmedBy?.vaccinesDue} contacts={contacts} />
                  {/* THE CARD THE CUSTOMER UPLOADED OPENS FROM HERE (deposit-proof
                      AC15, AC15a): read-only, through the same authenticated
                      viewer as a screenshot. No Accept — the verdict and the
                      card's states are vaccination-cards'. */}
                  {d.cardProofId && (
                    <button type="button" className="dog-card-view" aria-label={`View ${d.name.trim() || 'this dog'}’s vaccination card`} onClick={() => setCardOpen({ id: d.cardProofId!, name: d.name, mime: d.cardProofMime })}>
                      <Icon name="eye" size={12} />View card
                    </button>
                  )}
                </label>
                <input
                  value={d.vaccinesDue}
                  placeholder="e.g. JUN 26"
                  onChange={(e) => updD(i, { vaccinesDue: e.target.value })}
                />
              </div>
            </div>
            {/* THE BOOKING MODAL'S OWN CONTROL (Freddi, 2026-09-16), not a
                lookalike — `CareTile` is lifted into common and both screens
                render the same thing, so the two cannot drift apart.
                
                FULL WIDTH, ON ITS OWN ROW. The tile carries an icon, a title
                and a sub-line; squeezed into half a field-row beside Size it
                wrapped and sat unevenly against the inputs. Given the width it
                was drawn for, it lines up with the card's other rows.
                
                The customer's answer at the granularity it was given — asked
                once per dog in the portal and flattened onto the household
                until 0033. Whether anyone ANSWERED is the mark's job, and a dog
                nobody confirmed simply carries none (AC10). */}
            <div className="dog-care" style={{ marginTop: 2 }}>
              {/* TWO TILES, SIDE BY SIDE (Freddi, 2026-09-16), both of them the
                  booking modal's own control and both showing what the customer
                  said at registration. Meds had no field on this screen at all
                  — the column was written only by the portal and read only by
                  the booking flags, so the desk could not see it. */}
              {/* AN OFF TILE SAYS NOTHING, SO NOBODY SIGNS IT (Freddi,
                  2026-09-16). The avatar rides a tile only while it is ON. An
                  unticked box looks the same whether the customer answered no
                  or never reached the question, so a mark beside it claimed a
                  distinction Patricia cannot see. `marksFor` stopped writing a
                  false boolean; this stops drawing the ones already stored. */}
              <CareTile
                on={d.medsOn}
                onToggle={(v) => updD(i, { medsOn: v, ...(v ? {} : { meds: '' }) })}
                tone="meds"
                icon="medication"
                title="Medication"
                sub={d.medsOn ? 'On medication' : 'Not on any'}
                mark={d.medsOn ? <Mark by={d.confirmedBy?.meds} contacts={contacts} /> : null}
              />
              <CareTile
                on={d.keepApart === true}
                onToggle={(v) => updD(i, { keepApart: v })}
                tone="mix"
                icon="do-not-mix"
                title="Keep apart"
                sub="Do not mix with other households"
                mark={d.keepApart === true ? <Mark by={d.confirmedBy?.keepApart} contacts={contacts} /> : null}
              />
            </div>
            {/* The instructions ARE the fact — the tile is only whether there
                are any. Shown when it is on, so turning it on has somewhere to
                type and the words the household sent are never hidden behind a
                toggle. */}
            {d.medsOn && (
              <div className="field" style={{ marginTop: 8 }}>
                <label>Instructions<Mark by={d.confirmedBy?.meds} contacts={contacts} /></label>
                {/* Empty on the way in, with the placeholder carrying the ask —
                    the desk's own `.field input::placeholder` gives it the
                    muted colour and body weight every other field here uses. */}
                <input
                  value={d.meds}
                  placeholder="What, how much, and when"
                  onChange={(e) => updD(i, { meds: e.target.value })}
                />
              </div>
            )}
          </div>
        ))}
        <button type="button" className="owner-contact-add" onClick={() => setDogList((p) => [...p, { name: '', breed: '', size: 'big', vaccinesDue: '', medsOn: false, meds: '' }])}>+ Add dog</button>
        </DisclosureRegion>
      </div>
      </div>

      <ModalFooter
        onCancel={close}
        onSave={save}
        saveLabel="Save"
        blockers={blockers}
        keepOpen={owner ? { on: keepOpen.on, onChange: keepOpen.setOn } : null}
        danger={
          isCreate
            ? null
            : isArchived
              ? { label: 'Restore', onClick: toggleArchive }
              : { label: 'Archive', confirm: { message: 'Archive this household? It leaves the active list — you can restore it.', confirmLabel: 'Archive', onConfirm: toggleArchive } }
        }
      />
    </div>
  );
}
